import { useState, useEffect, useCallback } from "react";
import { getSocietyDocuments, uploadSocietyDocuments, deleteSocietyDocument } from "../../services/societyService";
import { Trash2, FileText, Upload, X } from "lucide-react";
import { getCurrentRoleKey } from "../../services/authService";
import styles from "../../pages/Property/explorer/PropertyExplorer.module.css";
import { format } from "date-fns";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Select from "../ui/Select";
import TablePagination from "../common/TablePagination";

import {
  normalizePropertyType,
  PROPERTY_CATEGORIES,
} from "../../utils/propertyStructure";

const INDUSTRIAL_DOC_TYPES = [
  "Environmental & Pollution NOC (SPCB)",
  "Factory License / Inspector Clearance",
  "Fire & Life Safety Clearance",
  "Emergency Evacuation & SOP Guidelines",
  "Hazardous Material & Waste Manifest",
  "Machinery & Boiler Safety Certification",
  "Electrical Substation & Transformer NOC",
  "Corporate Non-Disclosure Agreement (NDA)",
  "Industrial Lease & Deed Agreement",
  "Other",
].map((t) => ({ label: t, value: t }));

const COMMERCIAL_DOC_TYPES = [
  "Shop & Establishment License",
  "Trade License",
  "Fire Safety NOC",
  "Commercial Lease Agreement",
  "Signage / Facade Approval",
  "FSSAI / Food & Health Clearance",
  "Other",
].map((t) => ({ label: t, value: t }));

const RESIDENTIAL_DOC_TYPES = [
  "Undertaking / Affidavit-Cum-Declaration",
  "Acknowledgement of Purchaser",
  "Gas (PNG) No Objection Certificate (NOC)",
  "Electricity No Objection Certificate (NOC)",
  "Internal No Dues Confirmation",
  "No Dues Certificate",
  "Disclaimer",
  "Allotment letter",
  "Registration letter",
  "Other",
].map((t) => ({ label: t, value: t }));

const PropertyDocuments = ({ societyId, propertyType }) => {
  const normalizedCategory = normalizePropertyType(propertyType);
  const isIndustrial = normalizedCategory === PROPERTY_CATEGORIES.INDUSTRIAL;
  const isCommercial = normalizedCategory === PROPERTY_CATEGORIES.COMMERCIAL;

  const DOCUMENT_TYPES = isIndustrial
    ? INDUSTRIAL_DOC_TYPES
    : isCommercial
    ? COMMERCIAL_DOC_TYPES
    : RESIDENTIAL_DOC_TYPES;

  const roleKey = getCurrentRoleKey();
  const isSuperAdmin = roleKey === "super-admin";
  const isSocietyAdmin = roleKey === "society-admin";
  const canManage = isSuperAdmin || isSocietyAdmin || roleKey === "admin" || roleKey === "property-manager" || roleKey === "manager";

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Upload State
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Filter State
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterFileType, setFilterFileType] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Delete State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    const res = await getSocietyDocuments(societyId);
    if (res?.success) {
      setDocuments(res.data || []);
    }
    setLoading(false);
  }, [societyId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;

    const newFiles = selectedFiles.map(file => {
      const parts = file.name.split('.');
      const ext = parts.length > 1 ? `.${parts.pop()}` : '';
      const baseName = parts.join('.');
      
      return {
        file,
        baseName,
        ext,
        type: "Other"
      };
    });

    setFilesToUpload(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setFilesToUpload(prev => prev.filter((_, i) => i !== index));
  };

  const updateFileMeta = (index, field, value) => {
    setFilesToUpload(prev => {
      const copy = [...prev];
      copy[index][field] = value;
      return copy;
    });
  };

  const handleUpload = async () => {
    if (!filesToUpload.length) return;
    setUploading(true);

    const files = filesToUpload.map(f => f.file);
    const metadata = filesToUpload.map(f => ({ name: `${f.baseName}${f.ext}`, type: f.type }));

    const res = await uploadSocietyDocuments(societyId, files, metadata);
    if (res?.success) {
      setFilesToUpload([]);
      setIsUploadModalOpen(false);
      fetchDocuments();
    }
    setUploading(false);
  };

  const confirmDelete = (doc) => {
    setDocToDelete(doc);
    setDeleteModalOpen(true);
  };

  const executeDelete = async () => {
    if (!docToDelete) return;
    const res = await deleteSocietyDocument(societyId, docToDelete.document_id);
    if (res?.success) {
      fetchDocuments();
    }
    setDeleteModalOpen(false);
    setDocToDelete(null);
  };

  const availableFileTypes = Array.from(new Set(documents.map(doc => {
    const parts = (doc.document_name || "").split(".");
    return parts.length > 1 ? `.${parts.pop().toLowerCase()}` : "";
  }).filter(Boolean)));

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.document_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType ? doc.document_type === filterType : true;
    
    let matchesFileType = true;
    if (filterFileType) {
      const parts = (doc.document_name || "").split(".");
      const ext = parts.length > 1 ? `.${parts.pop().toLowerCase()}` : "";
      matchesFileType = ext === filterFileType;
    }

    return matchesSearch && matchesType && matchesFileType;
  });

  const paginatedDocuments = filteredDocuments.slice((page - 1) * limit, page * limit);

  return (
    <div style={{ marginTop: "16px" }}>
      {canManage && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", justifyContent: "space-between", marginBottom: "16px", alignItems: "center" }}>
          <div style={{ display: "flex", gap: "12px", flex: 1, minWidth: "300px", flexWrap: "wrap", alignItems: "center" }}>
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              style={{
                width: "240px",
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                fontSize: "13.5px",
                outline: "none",
                fontFamily: "inherit",
              }}
            />
            <div style={{ width: "200px" }}>
              <Select
                options={[{ label: "All Types", value: "" }, ...DOCUMENT_TYPES]}
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setPage(1);
                }}
                size="sm"
                placeholder="Document Type"
              />
            </div>
            <div style={{ width: "150px" }}>
              <Select
                options={[{ label: "All Formats", value: "" }, ...availableFileTypes.map(ext => ({ label: ext.toUpperCase(), value: ext }))]}
                value={filterFileType}
                onChange={(e) => {
                  setFilterFileType(e.target.value);
                  setPage(1);
                }}
                size="sm"
                placeholder="Format"
              />
            </div>
            {(searchTerm || filterType || filterFileType) && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setSearchTerm("");
                  setFilterType("");
                  setFilterFileType("");
                  setPage(1);
                }}
                style={{ color: "#64748b", display: "flex", alignItems: "center", gap: "6px" }}
              >
                <X size={14} /> Clear
              </Button>
            )}
          </div>
          <Button 
            variant="primary" 
            size="md"
            onClick={() => setIsUploadModalOpen(true)}
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <Upload size={16} /> Upload Documents
          </Button>
        </div>
      )}

      <Modal isOpen={isUploadModalOpen} onClose={() => { setIsUploadModalOpen(false); setFilesToUpload([]); }} title="Upload Documents">
        <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "24px" }}>
          
          <label style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            padding: "32px 20px", border: "2px dashed #cbd5e1", borderRadius: "12px",
            background: "#f8fafc", cursor: "pointer", transition: "border-color 0.2s"
          }}>
            <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "#e0e7ff", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
              <Upload size={24} color="#4f46e5" />
            </div>
            <div style={{ fontSize: "16px", fontWeight: "600", color: "#1e293b", marginBottom: "4px" }}>
              Click to browse or drag files here
            </div>
            <div style={{ fontSize: "13px", color: "#64748b" }}>
              Support for single or bulk upload.
            </div>
            <input type="file" multiple onChange={handleFileChange} style={{ display: "none" }} />
          </label>

          {filesToUpload.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div style={{ fontSize: "14px", fontWeight: "600", color: "#334155", borderBottom: "1px solid #e2e8f0", paddingBottom: "8px" }}>
                Selected Files ({filesToUpload.length})
              </div>
              <div style={{ maxHeight: "300px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "12px", paddingRight: "4px" }}>
                {filesToUpload.map((f, i) => (
                  <div key={i} style={{ 
                    display: "flex", gap: "16px", alignItems: "center", background: "#ffffff", 
                    padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
                  }}>
                    <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <FileText size={20} color="#3b82f6" />
                    </div>
                    <div style={{ flex: 1, display: "flex", flexDirection: "row", gap: "12px", minWidth: 0, alignItems: "center" }}>
                      <div style={{ flex: 1, display: "flex", border: "1px solid #cbd5e1", borderRadius: "6px", overflow: "hidden" }}>
                        <input 
                          type="text"
                          value={f.baseName} 
                          onChange={(e) => updateFileMeta(i, "baseName", e.target.value)} 
                          placeholder="Document Name" 
                          style={{ flex: 1, padding: "8px 12px", border: "none", outline: "none", fontSize: "13.5px", fontFamily: "inherit" }}
                        />
                        <span style={{ padding: "8px 12px", background: "#f1f5f9", color: "#64748b", fontSize: "13px", fontWeight: "600", borderLeft: "1px solid #cbd5e1" }}>
                          {f.ext}
                        </span>
                      </div>
                      <div style={{ flex: 1, minWidth: "180px" }}>
                        <Select
                          options={DOCUMENT_TYPES}
                          value={f.type}
                          onChange={(e) => updateFileMeta(i, "type", e.target.value)}
                          size="md"
                        />
                      </div>
                    </div>
                    <button type="button" onClick={() => removeFile(i)} style={{ 
                      background: "#fee2e2", border: "none", color: "#ef4444", 
                      cursor: "pointer", padding: "8px", borderRadius: "50%",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      transition: "all 0.2s"
                    }}>
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {filesToUpload.length > 0 && (
            <Button 
              variant="primary" 
              onClick={handleUpload} 
              loading={uploading} 
              disabled={uploading}
              size="lg"
              style={{ width: "100%", borderRadius: "8px", fontWeight: "600", fontSize: "15px" }}
            >
              Upload {filesToUpload.length} Document{filesToUpload.length > 1 ? "s" : ""}
            </Button>
          )}
        </div>
      </Modal>

      {loading ? (
        <div style={{ padding: "48px 0", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", color: "var(--color-muted, #64748b)" }}>
          <div style={{ width: 32, height: 32, border: "3px solid #e2e8f0", borderTopColor: "var(--color-dark-bg, #27306f)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <span style={{ fontSize: "13px", fontWeight: 500 }}>Loading documents...</span>
        </div>
      ) : documents.length === 0 ? (
        <div className={styles.empty}>No documents found for this property.</div>
      ) : filteredDocuments.length === 0 ? (
        <div className={styles.empty}>No documents match the applied filters.</div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Document Name</th>
                <th className={styles.th}>Type</th>
                <th className={styles.th}>Uploaded At</th>
                <th className={styles.th} style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDocuments.map((doc) => (
                <tr key={doc.document_id}>
                  <td className={styles.td} style={{ fontWeight: "500", color: "#0f172a" }}>
                    <a href={doc.document_url} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "none", display: "flex", alignItems: "center", gap: "8px" }}>
                      <FileText size={16} color="var(--color-dark-bg, #27306f)" />
                      {doc.document_name}
                    </a>
                  </td>
                  <td className={styles.td}>
                    <span style={{ padding: "4px 8px", background: "#f1f5f9", borderRadius: "100px", fontSize: "12px", fontWeight: "500", color: "#475569" }}>
                      {doc.document_type}
                    </span>
                  </td>
                  <td className={styles.td} style={{ color: "#64748b" }}>
                    {format(new Date(doc.uploaded_at), "MMM d, yyyy h:mm a")}
                  </td>
                  <td className={styles.td} style={{ textAlign: "right" }}>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                      <a 
                        href={doc.document_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={styles.actionBtn}
                      >
                        View
                      </a>
                      {canManage && (
                        <button 
                          className={styles.actionBtn} 
                          onClick={() => confirmDelete(doc)}
                          style={{ color: "#ef4444" }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination
            totalItems={filteredDocuments.length}
            page={page}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </div>
      )}

      <Modal isOpen={deleteModalOpen} onClose={() => { setDeleteModalOpen(false); setDocToDelete(null); }} title="Confirm Deletion">
        <div style={{ padding: "20px" }}>
          <p style={{ margin: "0 0 20px 0", color: "#334155", fontSize: "15px", lineHeight: "1.5" }}>
            Are you sure you want to delete the document <strong>&quot;{docToDelete?.document_name}&quot;</strong>?<br/>
            This action cannot be undone.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            <Button variant="ghost" onClick={() => { setDeleteModalOpen(false); setDocToDelete(null); }}>
              Cancel
            </Button>
            <Button variant="danger" onClick={executeDelete}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PropertyDocuments;


